// Import the AG Grid modules
import { Security } from "@okta/okta-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { type ErrorRouteComponent, Link, RouterProvider } from "@tanstack/react-router";
import { type AbstractLink, NavProvider } from "@teamworksdev/nav";
import { Spinner, Toaster, TooltipProvider } from "@teamworksdev/react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { type ComponentProps, Suspense } from "react";
import config from "~/config";
import NotFound from "~/modules/NotFound";
import { queryClient } from "~/queryClient";
import { router } from "~/router";
import { oktaAuth } from "~/services/okta";
import { useLaunchDarkly } from "./services/launchDarkly";

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

type RestoreOriginalUriFunction = ComponentProps<typeof Security>["restoreOriginalUri"];

const Axle = () => {
  const restoreOriginalUri: RestoreOriginalUriFunction = async () => {
    const basePath = !config.app.baseUrl || config.app.baseUrl === "/" ? "" : config.app.baseUrl;
    window.location.href = `${basePath}/orgs`; // want this to go to orgs
  };

  useLaunchDarkly();

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Suspense fallback={null}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <NavProvider Link={NavLink}>
              <RouterProvider
                basepath={config.app.baseUrl}
                defaultErrorComponent={ErrorFallback}
                defaultNotFoundComponent={NotFound}
                defaultPendingComponent={PendingFallback}
                router={router}
              />
            </NavProvider>
            <Toaster position="bottomLeft" />
          </TooltipProvider>
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </QueryClientProvider>
      </Suspense>
    </Security>
  );
};

const PendingFallback = () => (
  <div className="w-full h-full flex items-center justify-center p-6">
    <Spinner size="lg" />
  </div>
);

const ErrorFallback: ErrorRouteComponent = ({ error }) => {
  return (
    <div className="flex flex-col items-center justify-center w-full h-screen max-h-full bg-black space-y-4">
      <span className="text-white text-3xl font-bold">Oops! Something went wrong</span>
      <span className="text-white/80 text-lg">{error.message}</span>
    </div>
  );
};

const NavLink = ({ activeClassName, children, ...props }: ComponentProps<typeof AbstractLink>) => (
  <Link activeProps={{ className: activeClassName }} activeOptions={{ exact: true, includeSearch: false }} {...props}>
    {children}
  </Link>
);

export default Axle;
